import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    useTheme,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ToggleOn as ToggleOnIcon,
    TextFields as TextFieldsIcon,
    Warning as WarningIcon,
    Numbers as NumbersIcon
} from '@mui/icons-material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';

interface PreferencesTabProps {
    consentData: SandboxConsentDetails;
}

const PreferencesTab: React.FC<PreferencesTabProps> = ({ consentData }) => {
    const theme = useTheme();
    
    // Get collection point level preferences from API response
    const preferences = useMemo(() => {
        return consentData.collectionPointPreferences || [];
    }, [consentData]);

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        const total = preferences.length;
        const required = preferences.filter(p => p.isRequired).length;
        const optional = total - required;
        
        // Count by type
        const typeCounts = preferences.reduce((acc, pref) => {
            acc[pref.type] = (acc[pref.type] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);
        
        return { total, required, optional, typeCounts };
    }, [preferences]);

    const getPreferenceTypeIcon = (type: string) => {
        switch (type.toLowerCase()) {
            case 'boolean':
                return <ToggleOnIcon />;
            case 'text':
                return <TextFieldsIcon />;
            case 'multiselect':
                return <NumbersIcon />;
            default:
                return <WarningIcon />;
        }
    };

    const getPreferenceTypeColor = (type: string) => {
        switch (type.toLowerCase()) {
            case 'boolean':
                return 'primary';
            case 'text':
                return 'secondary';
            case 'multiselect':
                return 'success';
            default:
                return 'default';
        }
    };

    const formatPreferenceValue = (preference: any, selectedOptions: any[]) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            return 'No selection';
        }

        if (preference.allowMultipleSelection) {
            return selectedOptions.map((opt: any) => opt.label).join(', ');
        } else {
            return selectedOptions[0]?.label || 'Unknown';
        }
    };

    return (
        <Box>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                            {summaryStats.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Preferences
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                            {summaryStats.required}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Required
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                            {summaryStats.optional}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Optional
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                            {Object.keys(summaryStats.typeCounts).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Types
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Preferences List */}
            {preferences.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Collection Point Preferences
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        This collection point does not have any collection point level preferences.
                        Purpose-specific preferences are shown in the Purposes tab.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {preferences.map((preference) => (
                        <Grid item xs={12} md={6} key={preference.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardHeader
                                    title={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="h6">
                                                {preference.name}
                                            </Typography>
                                            <Chip
                                                icon={getPreferenceTypeIcon(preference.type)}
                                                label={preference.type}
                                                color={getPreferenceTypeColor(preference.type) as any}
                                                size="small"
                                                variant="outlined"
                                            />
                                            {preference.isRequired && (
                                                <Chip
                                                    icon={<WarningIcon />}
                                                    label="Required"
                                                    color="warning"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    {preference.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {preference.description}
                                        </Typography>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    {/* Available Options */}
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
                                        Available Options:
                                    </Typography>
                                    <List dense>
                                        {preference.options.map((option) => (
                                            <ListItem key={option.id} sx={{ py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: option.isDefaultSelected ? 'primary.main' : 'grey.300'
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={option.name}
                                                    secondary={option.description}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                                {option.isDefaultSelected && (
                                                    <Chip
                                                        label="Default"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Divider sx={{ my: 2 }} />

                                    {/* User Selection */}
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
                                        User Selection:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                        <Typography variant="body2">
                                            {formatPreferenceValue(preference, preference.selectedOptions || [])}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default PreferencesTab;
